import {DATA_INTERVAL} from '../../../MobxStores/DataStore';
import {ProcedureRequester} from '../../../MobxStores/RequestApi/ProcedureRequester';

export const cloneObj = (obj) => obj ? JSON.parse(JSON.stringify(obj)) : {};

export const DEFAULT_SETTINGS = {
    VideoAutoPlay: true,
    ClickToSelect: false,
    AutoTTS: false,
    AllowNotes: true,
    AllowSkipStep: true,
};

export const buildSaveJson = (procedure, edited) => ({
    ProcedureIdentifier: procedure.ProcedureIdentifier,
    ProcedureName: edited.name,
    ProcedureDescription: edited.description,
    AIInstructions: edited.aiInstructions,
    Settings: edited.settings,
    ThreeDSettings: edited.threeDSettings,
    PermittedTrainingAndFieldRoles: edited.permittedRoles,
    ThreeDModelsConnected: edited.threeDModels,
    AllowRun: edited.allowRun,
});

export const saveProcedure = async (json, workspace) => {
    return ProcedureRequester.createOrUpdateProcedure(json, workspace);
};

export const saveMirroredPermittedRoles = async (procedureId, permittedRoles, workspace) => {
    return ProcedureRequester.updateMirroredPermittedRoles(procedureId, permittedRoles, workspace);
};

const applyRolesPatch = (procedureId, permittedRoles) => (proc) =>
    proc?.ProcedureIdentifier === procedureId
        ? {...proc, PermittedTrainingAndFieldRoles: {...permittedRoles}}
        : proc;

const patchProjectsProceduresMap = (dataStore, procedureId, patch) => {
    for (const [, procMap] of dataStore.projectsProceduresMap) {
        if (procMap?.has?.(procedureId)) procMap.set(procedureId, patch(procMap.get(procedureId)));
    }
};

const patchProcedureStore = (procedureStore, procedureId, patch) => {
    if (procedureStore.fetchedProceduresMap?.has?.(procedureId)) {
        procedureStore.fetchedProceduresMap.set(procedureId, patch(procedureStore.fetchedProceduresMap.get(procedureId)));
    }
    if (procedureStore.currentProcedure?.ProcedureIdentifier === procedureId) {
        procedureStore.currentProcedure = patch(procedureStore.currentProcedure);
    }
};

const patchModalAndLocal = (modalStore, setProcedure, patch) => {
    const updated = patch(modalStore.modalData?.procedure);
    if (modalStore.modalData?.procedure) modalStore.modalData.procedure = updated;
    if (updated) setProcedure(updated);
};

export const patchMirroredProcedureRoles = (procedureId, permittedRoles, {dataStore, procedureStore, modalStore, setProcedure}) => {
    const patch = applyRolesPatch(procedureId, permittedRoles);
    patchProjectsProceduresMap(dataStore, procedureId, patch);
    patchProcedureStore(procedureStore, procedureId, patch);
    patchModalAndLocal(modalStore, setProcedure, patch);
};

export const refreshProcedureInStores = (updated, {dataStore, procedureStore, modalStore, setProcedure}) => {
    const projectId = updated.ProjectRef;
    const map = dataStore.projectsProceduresMap.get(projectId);
    if (map) {
        map.set(updated.ProcedureIdentifier, updated);
        dataStore.set('projectsProceduresMap', projectId, map);
        dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
    }
    patchProcedureStore(procedureStore, updated.ProcedureIdentifier, () => updated);
    patchModalAndLocal(modalStore, setProcedure, () => updated);
};

export const generateAIInstructionsRequest = async (currentProcedure, editedAIInstructions, workspace, projectId) => {
    const data = {
        ProcedureName: currentProcedure.ProcedureName,
        ProcedureDescription: currentProcedure.ProcedureDescription,
        ParentFolder: currentProcedure.parentFolder,
        FilteredNestedNodes: currentProcedure.NestedNodes?.map((n) => ({StepHeader: n.StepHeader, StepText: n.StepText})),
        AIInstructions: editedAIInstructions,
    };
    const result = await ProcedureRequester.generateAIInstructions(data, workspace, projectId);
    if (result === 'Error communicating with OpenAI API:') throw new Error(result);
    return result;
};
